import { CamelCasedPropertiesDeep } from 'type-fest';
import { z } from 'zod/v4';
import { calculationTypeSchema, createThresholdOptionsSchema, stepOptionsSchema, unitOptionsSchema } from '../../utils';
import { createDefinitionSchema } from '../../utils/helpers';
import { modeOptionSchema, sortOptionSchema } from '../../utils/options';
import { seriesOverridesSchema } from '../../utils/overrides';

export interface BarChartData {
  label: string;
  value: number | null;
  comparisonValue?: number | null;
}

/**
 * Normalized click event produced by both the ECharts-based BarChartBase and
 * the TopListBase variants, exposing only the fields consumers actually need.
 */
export interface BarClickEvent {
  name: string;
  event: { event: MouseEvent };
  data: { value: number | null };
}

export const barChartThresholdOptionsSchema = createThresholdOptionsSchema(stepOptionsSchema);
export interface BarChartStepOption extends z.TypeOf<typeof stepOptionsSchema> {}
export interface BarChartThresholdOptions extends z.TypeOf<typeof barChartThresholdOptionsSchema> {}

export const DEFAULT_DISPLAYED_BARS_LIMIT = 25;
export const DEFAULT_TOPLIST_BARS_LIMIT = 10000;
export const MAX_BARS_LIMIT_DESCRIPTION = `Maximum number of bars to display in the chart, defaults to ${DEFAULT_DISPLAYED_BARS_LIMIT} when not specified.`;

/**
 * Type for the spec-driven `DashboardPanel` variant of the bar chart.
 */
export interface BarChartSpec extends z.TypeOf<typeof barChartSpecSchema> {}

/**
 * Zod schema for the spec-driven `DashboardPanel` variant of the bar chart.
 */
export const barChartSpecSchema = z.object({
  calculation: calculationTypeSchema.describe('DEPRECATED').optional(),
  unit: unitOptionsSchema.optional(),
  sort: sortOptionSchema.optional(),
  mode: modeOptionSchema.optional(),
  overrides: seriesOverridesSchema.optional(),
  thresholds: barChartThresholdOptionsSchema.optional(),
  limit: z.number().optional().describe(MAX_BARS_LIMIT_DESCRIPTION),
});

/**
 * The schema for a BarChart `DashboardPanel` definition.
 */
export interface BarChartDefinition extends z.TypeOf<typeof barChartDefinitionSchema> {}
export const barChartDefinitionSchema = createDefinitionSchema('BarChart', barChartSpecSchema);

/**
 * Version of the Zod schema to be used for base types for the props-driven `Viz` variant of the bar chart.
 *
 * Currently, it is the same as the spec-driven variant, but it may need to change in the future to
 * do things like flatten a specific property or some other transform (examples can be found in pie chart).
 */
export const barChartVizSchema = barChartSpecSchema;

/**
 * Base type for the props-driven `Viz` variant of the bar chart generated from the Zod schema and
 * converting all snake_case properties to camelCase. This interface should be extended in `viz-framework`
 * to do the following:
 * - Add additional types for `Viz` only use cases.
 * - Extend properties to add more meaningful jsdoc. This is necessary because both `z.Typeof` and
 *   `CamelCasedPropertiesDeep` lose jsdoc.
 */
export interface BarChartPropsBase extends CamelCasedPropertiesDeep<z.TypeOf<typeof barChartVizSchema>> {}
